import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Accessibility, Bell, Building2, Key, Languages, Palette, Sparkles, UserSquare2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Alert, Button } from '../components/ui';
import AdminShell from '../components/admin/AdminShell';
import useConnectionState from '../components/admin/useConnectionState';
import ThemeSettings from '../components/admin/ThemeSettings';
import AISettings from '../components/admin/AISettings';
import PersonaSettings from '../components/admin/PersonaSettings';
import AccessTokens from '../components/admin/AccessTokens';
import TenantInfo from '../components/admin/TenantInfo';
import {
  AccessibilitySettings,
  LanguageSettings,
  NotificationSettings
} from '../components/settings/PersonalPreferences';

/** Sections that stayed on `/admin` when the surface was split (spec §8.1). */
const MOVED_TO_ADMIN = ['sources', 'documents', 'paths', 'groups', 'users', 'tenant-defaults', 'needs'];

const SETTINGS_SECTIONS = ['theme', 'language', 'ai', 'persona', 'tokens', 'tenant', 'notifications', 'accessibility'];
const DEFAULT_SECTION = 'theme';

function sectionFromPath(pathname) {
  const segment = pathname.split('/').filter(Boolean).at(-1);
  return SETTINGS_SECTIONS.includes(segment) ? segment : DEFAULT_SECTION;
}

/**
 * `/settings` — everything that belongs to the signed-in person rather than to
 * the tenant (spec §8.1, §10.6). Split out of the old combined `/admin`
 * surface; every pre-split `/admin/<pref>` deep link is forwarded here by
 * `AdminPage`, and the reverse forward lives below.
 */
export default function SettingsPage() {
  const { t } = useTranslation();
  const { user, loading: authLoading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const online = useConnectionState();
  const [activeSection, setActiveSection] = useState(() => sectionFromPath(location.pathname));

  const legacySegment = location.pathname.split('/').filter(Boolean).at(-1);
  const movedSection = MOVED_TO_ADMIN.includes(legacySegment) ? legacySegment : null;

  useEffect(() => {
    if (movedSection) navigate(`/admin/${movedSection}`, { replace: true });
  }, [movedSection, navigate]);

  useEffect(() => {
    const fromPath = sectionFromPath(location.pathname);
    if (fromPath !== activeSection) setActiveSection(fromPath);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  function selectSection(id) {
    setActiveSection(id);
    navigate(`/settings/${id}`, { replace: true });
  }

  if (movedSection) return null;

  if (authLoading) {
    return (
      <div className="etch-view etch-center" role="status" aria-live="polite">
        {t('states.loading')}
      </div>
    );
  }

  if (!user) {
    return (
      <div className="etch-view etch-stack" data-gap="4">
        <Alert tone="warning" title={t('states.sessionExpired')}>
          {t('auth.sessionExpired')}
        </Alert>
        <Button onClick={() => navigate('/login')}>{t('auth.login')}</Button>
      </div>
    );
  }

  const items = [
    { id: 'theme', label: t('settings.theme.title'), icon: Palette, group: 'look', groupLabel: t('settings.groupLook') },
    { id: 'language', label: t('settings.language'), icon: Languages, group: 'look' },
    { id: 'accessibility', label: t('settings.accessibility.title'), icon: Accessibility, group: 'look' },
    { id: 'notifications', label: t('settings.notifications'), icon: Bell, group: 'look' },
    { id: 'ai', label: t('settings.providerTitle'), icon: Sparkles, group: 'ai', groupLabel: t('settings.groupAi') },
    { id: 'persona', label: t('settings.personaTitle'), icon: UserSquare2, group: 'ai' },
    { id: 'tokens', label: t('admin.tokens'), icon: Key, group: 'account', groupLabel: t('settings.groupAccount') },
    { id: 'tenant', label: t('admin.tenant'), icon: Building2, group: 'account' }
  ];

  return (
    <AdminShell
      title={t('nav.settings')}
      idPrefix="settings"
      items={items}
      activeId={activeSection}
      onSelect={selectSection}
    >
      {!online && (
        <Alert tone="warning" title={t('states.offline')}>
          {t('settings.offlineHint')}
        </Alert>
      )}

      {activeSection === 'theme' && <ThemeSettings />}
      {activeSection === 'language' && <LanguageSettings />}
      {activeSection === 'accessibility' && <AccessibilitySettings />}
      {activeSection === 'notifications' && <NotificationSettings />}
      {activeSection === 'ai' && <AISettings />}
      {activeSection === 'persona' && <PersonaSettings />}
      {activeSection === 'tokens' && <AccessTokens scope="me" />}
      {activeSection === 'tenant' && <TenantInfo />}
    </AdminShell>
  );
}
